<template>
  <div>
    <div class="d-flex align-items-center">
      <h4 class="pages-title mb-0">{{ $t('users.form.invite_dashboard') }}</h4>
    </div>
    <section class="userlist-massaction">
      <div class="page-container">
        <!-- start::table -->
        <div class="massaction-invite-toolbar">
          <p class="md-title-1">
            {{ tableData.length - 1 }}
            <template v-if="tableData.length - 1 == 1"> {{ $t('users.form.userSelected')}}</template>
            <template v-else> {{ $t('users.form.usersSelected')}} </template>
          </p>
          <button
            style="margin-inline-start: 30px"
            class="invite-all"
            :class="{ 'invite-loading': massActionIsStarted }"
            @click="inviteAll"
            :disabled="$v.$pending || $v.$invalid || massActionIsStarted"
          >
            <i
              v-if="massActionLoaderStatus"
              style="margin-inline-end: 5px"
              class="bx bx-loader-alt bx-spin font-size-16 align-middle rounded"
            ></i>
            {{ massActionLoaderStatus ? $t('common.inviting') : $t('common.inviteAll') }}
          </button>
        </div>
        <p class="vp-ml-40 vp-mb-20 vp-color-grey-3">
          <b>{{ $t('common.notes.note') }}:</b> {{ $t('inviteToDashboardMassAction.userAccess') }}
        </p>

        <md-table
          v-model="tableData"
          class="massaction-table"
          style="min-height: 470px"
        >
          <md-table-row slot="md-table-row" slot-scope="{ item, index }">
            <!-- start::all action -->
            <template v-if="item.all">
              <md-table-cell class="w-176" md-label="User">{{ $t('signInOut.all')}}</md-table-cell>
              <md-table-cell
                class="user-role-th user-role-dropdown"
                :md-label="$t('common.userRole')"
              >
                <multiselect
                  :disabled="massActionIsStarted"
                  class=""
                  open-direction="bottom"
                  v-model="tableData[0].role_id"
                  :options="roleOptions"
                  :hideSelected="false"
                  :allow-empty="true"
                  :searchable="true"
                  selectLabel=""
                  deselectLabel=""
                  trackBy="id"
                  label="name"
                  :placeholder="$t('common.select')+'...'"
                  @input="updateAllUsers"
                  @open="onMultiselectOpen"
                  @close="onMultiselectClose"
                />
              </md-table-cell>
              <md-table-cell :md-label="$t('common.site')">
                <div class="vp-my-4 invite">
                  <vp-treeselect
                    v-model="item.sites"
                    :key="index"
                    :options="sitesOptions"
                    :placeholder="$t('common.select')+'...'"
                    :showPlaceholderIcon="false"
                    width="300"
                    dropDownStyle="absolute"
                    backgroundColor="#ffffff"
                    :disabled="massActionIsStarted"
                    @input="siteMassAction"
                    @deselect="siteMassActionDeselect"
                    @onOpen="onTreeselectOpen"
                    @onClose="onTreeselectClose"
                  />
                </div>
              </md-table-cell>
              <md-table-cell :md-label="$t('common.userType')">
                <div class="vp-my-4">
                  <vp-treeselect
                    v-model="item.user_types"
                    :key="index"
                    :options="userTypesOptions"
                    :placeholder="$t('common.select')+'...'"
                    :showPlaceholderIcon="false"
                    dropDownStyle="absolute"
                    width="300"
                    backgroundColor="#ffffff"
                    :disabled="massActionIsStarted"
                    @input="userTypeMassAction"
                    @deselect="userTypeMassActionDeselect"
                    @onOpen="onTreeselectOpen"
                    @onClose="onTreeselectClose"
                  />
                </div>
              </md-table-cell>
              <md-table-cell :md-label="$t('common.error')"></md-table-cell>
              <md-table-cell
                class="w-195"
                :md-label="$t('common.email')"
              ></md-table-cell>
              <md-table-cell
                class="country-code-th"
                :md-label="$t('dashboard.grid.countryCode')"
              ></md-table-cell>
              <md-table-cell
                class="w-169"
                :md-label="$t('common.phoneNumber')"
              ></md-table-cell>
              <md-table-cell :md-label="$t('common.status')"></md-table-cell>
            </template>
            <!-- end::all action -->

            <!-- start::single action -->
            <template v-else>
              <!-- start::User name -->
              <md-table-cell md-label="User">{{
                item.full_name
              }}</md-table-cell>
              <!-- end::User name -->

              <!-- start::User role -->
              <md-table-cell
                :md-label="$t('common.userRole')"
                :id="'tooltip-target-role' + item.id"
                :class="{
                  'invalid-multiselect':
                    $v.tableData.$each.$iter[getPreviousStateForUserIndex(item)]
                      .role_id.$error,
                }"
              >
                <b-tooltip
                  class="user-role-column-tooltip"
                  v-if="
                    $v.tableData.$each.$iter[getPreviousStateForUserIndex(item)]
                      .role_id.$error
                  "
                  :target="'tooltip-target-role' + item.id"
                  triggers="focus"
                  variant="danger"
                  placement="top"
                >
                  {{ $t('inviteToDashboardMassAction.userRoleRequired') }}
                </b-tooltip>
                <!-- :class="{
                    'invalid-multiselect':
                      userRoleIsTouched &&
                      $v.tableData.$each.$iter[
                        getPreviousStateForUserIndex(item)
                      ].role_id.$error,
                  }" -->
                <multiselect
                  :disabled="massActionIsStarted"
                  class="user-role-dropdown multiselect-focus"
                  open-direction="bottom"
                  v-model="item.role_id"
                  :options="roleOptions"
                  :hideSelected="false"
                  :allow-empty="true"
                  :searchable="true"
                  selectLabel=""
                  deselectLabel=""
                  trackBy="id"
                  label="name"
                  :placeholder="$t('common.select')+'...'"
                  @input="
                    updateAllRole();
                    touchCurrentField(item, 'role_id');
                  "
                  @open="onMultiselectOpen"
                  @close="onMultiselectClose"
                />
              </md-table-cell>
              <!-- end::User role -->

              <!-- start::Sites -->
              <md-table-cell
                :md-label="$t('common.site')"
                :id="'tooltip-target-site' + item.id"
                :class="{
                  'invalid-cell':
                    $v.tableData.$each.$iter[getPreviousStateForUserIndex(item)]
                      .sites.$error,
                }"
              >
                <b-tooltip
                  class="user-role-column-tooltip"
                  v-if="
                    $v.tableData.$each.$iter[getPreviousStateForUserIndex(item)]
                      .sites.$error
                  "
                  :target="'tooltip-target-site' + item.id"
                  triggers="focus"
                  variant="danger"
                  placement="top"
                >
                  {{ $t('inviteToDashboardMassAction.siteRequired') }}
                </b-tooltip>
                <div class="vp-my-3">
                  <vp-treeselect
                    v-model="item.sites"
                    :key="index"
                    :options="sitesOptions"
                   :placeholder="$t('common.select')+'...'"
                    :showPlaceholderIcon="false"
                    width="300"
                    dropDownStyle="absolute"
                    backgroundColor="#ffffff"
                    :disabled="massActionIsStarted"
                    @onOpen="onTreeselectOpen"
                    @onClose="onTreeselectClose"
                    @input="
                      touchCurrentField(item, 'sites');
                      updateAllSites(item.sites);
                    "
                  />
                </div>
              </md-table-cell>
              <!-- end::Sites -->

              <!-- start::User Types -->
              <md-table-cell
                :md-label="$t('common.userType')"
                :id="'tooltip-target-usertype' + item.id"
                :class="{
                  'invalid-multiselect':
                    $v.tableData.$each.$iter[getPreviousStateForUserIndex(item)]
                      .user_types.$error,
                }"
              >
                <b-tooltip
                  class="user-role-column-tooltip"
                  v-if="
                    $v.tableData.$each.$iter[getPreviousStateForUserIndex(item)]
                      .user_types.$error
                  "
                  :target="'tooltip-target-usertype' + item.id"
                  triggers="focus"
                  variant="danger"
                  placement="top"
                >
                  {{ $t('inviteToDashboardMassAction.usertypeRequried') }}
                </b-tooltip>
                <div class="vp-my-3">
                  <vp-treeselect
                    v-model="item.user_types"
                    :key="index"
                    :options="userTypesOptions"
                    :showPlaceholderIcon="false"
                   :placeholder="$t('common.select')+'...'"
                    width="300"
                    backgroundColor="#ffffff"
                    :disabled="massActionIsStarted"
                    dropDownStyle="absolute"
                    @onOpen="onTreeselectOpen"
                    @onClose="onTreeselectClose"
                    @input="
                      touchCurrentField(item, 'user_types');
                      updateAllUsertypes(item.user_types);
                    "
                  />
                </div>
              </md-table-cell>
              <!-- end::User Types -->
              <!-- start::Error -->
              <md-table-cell
                class="table-cell-margin"
                :md-label="$t('common.error')"
                :class="{
                  'w-352': item.error && item.error,
                }"
              >
                <span
                  :class="{
                    'invaled-email-color': item.error && item.error,
                  }"
                >
                  {{ item.error || "-" }}
                </span></md-table-cell
              >
              <!-- end::Error -->

              <!-- start::Email Address -->
              <md-table-cell
                :md-label="$t('common.email')"
                class="email-cell table-cell-input-pading"
                :class="{
                  'invalid-input':
                    $v.tableData.$each.$iter[getPreviousStateForUserIndex(item)]
                      .email.$error && !item.error,
                }"
                :id="'tooltip-target-email' + item.id"
              >
                <md-field>
                  <b-tooltip
                    v-if="
                      $v.tableData.$each.$iter[
                        getPreviousStateForUserIndex(item)
                      ].email.$error && !item.error
                    "
                    :target="'tooltip-target-email' + item.id"
                    triggers="focus"
                    variant="primary"
                    placement="bottom"
                  >
                    {{ item.invalidEmailMessage }}
                  </b-tooltip>
                  <p class="display-contents">
                    <md-input
                      :class="{ dimmed: massActionIsStarted }"
                      :disabled="massActionIsStarted"
                      class="email-input"
                      v-model.trim="
                        $v.tableData.$each.$iter[index].email.$model
                      "
                      :placeholder="item.email.length ? item.email : '-'"
                      @input="
                        checkForErrors(item);
                        setIsEmailInputeBlured(false);
                      "
                      @blur="
                        touchCurrentField(item, 'email');
                        setIsEmailInputeBlured(true);
                      "
                      @keyup.enter="(ev) => ev.target.blur()"
                    />
                  </p>
                </md-field>
              </md-table-cell>
              <!-- end::Email Address -->

              <md-table-cell
                :md-label="$t('dashboard.grid.countryCode')"  
                :class="{
                  'invalid-multiselect':
                    $v.tableData.$each.$iter[getPreviousStateForUserIndex(item)]
                      .phone_code.$invalid,
                }"
                :id="'tooltip-target-code' + item.id"
              >
                <b-tooltip
                  v-if="
                    $v.tableData.$each.$iter[getPreviousStateForUserIndex(item)]
                      .phone_code.$invalid
                  "
                  :target="'tooltip-target-code' + item.id"
                  triggers="focus"
                  variant="danger"
                  placement="top"
                >
                  {{ $t('inviteToDashboardMassAction.countryCodeRequired') }}
                </b-tooltip>
                <multiselect
                  open-direction="bottom"
                  :disabled="massActionIsStarted"
                  v-model="item.phone_code"
                  class="country-code-dropdown"
                  :options="countryCodes"
                  :hideSelected="false"
                  :allow-empty="true"
                  :searchable="true"
                  selectLabel=""
                  deselectLabel=""
                  trackBy="dial_code"
                  label="finalCode"
                 :placeholder="$t('common.select')+'...'"
                  @input="() => (item.phone = '')"
                  @open="onMultiselectOpen"
                  @close="onMultiselectClose"
                />
              </md-table-cell>
              <!-- end::Country Code -->

              <!-- start::Phone Number -->
              <md-table-cell
                :md-label="$t('common.phoneNumber')"
                class="table-cell-input-pading"
                :class="{
                  'invalid-input':
                    (!item.phone && item.phone_code) ||
                    ($v.tableData.$each.$iter[
                      getPreviousStateForUserIndex(item)
                    ].phone.$error &&
                      !item.error),
                }"
                :id="'tooltip-target-phone' + item.id"
              >
                <md-field>
                  <b-tooltip
                    v-if="
                      (!item.phone && item.phone_code) ||
                      ($v.tableData.$each.$iter[
                        getPreviousStateForUserIndex(item)
                      ].phone.$error &&
                        !item.error)
                    "
                    :target="'tooltip-target-phone' + item.id"
                    triggers="focus"
                    variant="primary"
                    placement="bottom"
                  >
                    {{ item.invalidPhoneMessage }}
                  </b-tooltip>

                  <p class="display-contents">
                    <md-input
                      :disabled="massActionIsStarted"
                      :class="{ dimmed: massActionIsStarted }"
                      class="phone-input"
                      type="number"
                      pattern="\d*"
                      @wheel.prevent
                      v-model="item.phone"
                      @focus="() => (showCountryCodeColumn = true)"
                      :placeholder="item.phone.length ? item.phone : '-'"
                      @input="
                        checkForErrors(item);
                        setIsPhoneInputBlured(false);
                      "
                      @blur="
                        checkDuplicatedEmailOrPhone('phone');
                        touchCurrentField(item, 'phone');
                        setIsPhoneInputBlured(true);
                      "
                      @keyup.enter="(ev) => ev.target.blur()"
                    />
                  </p>
                </md-field>
              </md-table-cell>
              <!-- end::Phone Number -->

              <!-- start::Status -->
              <md-table-cell :md-label="$t('common.status')" class="table-cell-margin">
                <p
                  :class="{
                    'status-completed': item.status == 'Completed',
                    'status-in-progress': item.status == 'In progress',
                    'status-pending': item.status == 'Pending',
                  }"
                >
                  {{ item.status || "-" }}
                </p>
              </md-table-cell>
              <!-- end::Status -->
            </template>
            <!-- start::single action -->
          </md-table-row>
        </md-table>

        <b-modal
          id="redirectModal"
          size="lg"
          hide-header
          hide-footer
          no-close-on-esc
          no-close-on-backdrop
        >
          <ConfirmModal />
        </b-modal>

        <!-- end::table -->
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { countryCodes } from "../dashboards/codes";
import ConfirmModal from "./ConfirmModal.vue";
import { cloneDeep, difference } from "lodash";
import siteService from "@/services/sites";
import userTypeService from "@/services/userType";

export default {
  title: "User list mass action",
  name: "userlistmassaction",
  components: {
    ConfirmModal,
  },
  data() {
    return {
      userRoleIsTouched: false,
      isEmailInputeBlured: false,
      isPhoneInputBlured: false,
      test: true,
      intivialValidationCheck: true,
      massActionLoaderStatus: false,
      massActionIsStarted: false,
      countryCodes,
      isLoaded: false,
      showCountryCodeColumn: false,
      tableData: [],
      tableDataPreviousState: [],
      sitesOptions: [],
      userTypesOptions: [],
      regEmail:
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
    };
  },
  validations() {
    if (this.tableData.length && this.isLoaded) {
      return {
        tableData: {
          $each: {
            email: {
              //Check if email is in valid format && unique
              isEmailUnique: async (model, parentModel) => {
                const isAllModel = parentModel.all ? true : false;
                const hasEmail = model ? true : false;
                const userIndex =
                  this.getPreviousStateForUserIndex(parentModel);
                // const isPhoneInvalid = this.$v?.tableData?.$each?.$iter[
                //   userIndex
                // ]?.phone?.$invalid;

                const hasPhone =
                  this.$v?.tableData?.$each?.$iter[userIndex]?.phone;

                if (isAllModel || (!hasEmail && hasPhone)) return true;

                this.tableData[userIndex].invalidEmailMessage = "";

                if (!this.regEmail.test(model) && hasEmail) {
                  this.tableData[userIndex].invalidEmailMessage =
                    "Email address is invalid";
                  return false;
                }

                //Check if email value same as value from api
                const userPreviousState =
                  this.getPreviousStateForUser(parentModel);
                if (
                  userPreviousState?.email &&
                  userPreviousState?.email == model &&
                  hasEmail
                )
                  return true;

                if (hasEmail) {
                  let isAvailable = true;
                  let formData = new FormData();
                  formData.append("entity_id", this.currentEntity.id);
                  formData.append("field", "email");
                  formData.append("value", model);

                  let emailAlreadyUsedOnForm = [];
                  emailAlreadyUsedOnForm = this.tableData.filter(
                    (userData, indx) => {
                      return userData.email == model && indx != userIndex;
                    }
                  );
                  if (emailAlreadyUsedOnForm.length) {
                    this.tableData[userIndex].invalidEmailMessage =
                      "Email address already used";
                    return false;
                  }

                  if (this.$v.tableData.$each.$iter[userIndex].email.$dirty) {
                    await this.fetchUsersByEmailOrPhone(formData).then(
                      (data) => {
                        isAvailable = data.data.available;
                        if (!isAvailable) {
                          this.tableData[userIndex].invalidEmailMessage =
                            "Email address already exists";
                        }
                      }
                    );
                  }
                  // this.$v.tableData.$each.$iter[userIndex].email.$reset();
                  return isAvailable;
                }
              },
            },
            role_id: {
              roleRequired: (model, parentModel) => {
                if (parentModel?.all) return true;
                return model?.id ? true : false;
              },
            },
            sites: {
              sitesRequired: (model, parentModel) => {
                if (parentModel?.all) return true;
                return model?.length ? true : false;
              },
            },
            user_types: {
              userTypesRequired: (model, parentModel) => {
                if (parentModel?.all) return true;
                return model?.length ? true : false;
              },
            },
            phone_code: {
              phoneCodeValidation: (model, parentModel) => {
                const isAllModel = parentModel.all ? true : false;
                const hasPhoneNumber = parentModel.phone ? true : false;

                if (isAllModel) return true;

                const userPreviousState =
                  this.getPreviousStateForUser(parentModel);

                const userIndex =
                  this.getPreviousStateForUserIndex(parentModel);

                this.tableData[userIndex].invalidCountryCodeMessage = "";

                if (
                  userPreviousState?.phone &&
                  userPreviousState?.phone == parentModel.phone &&
                  hasPhoneNumber &&
                  !model
                ) {
                  return true;
                }

                if (
                  (userPreviousState?.phone &&
                    userPreviousState?.phone != parentModel.phone &&
                    hasPhoneNumber &&
                    !model) ||
                  (hasPhoneNumber && !model)
                ) {
                  this.tableData[userIndex].invalidCountryCodeMessage =
                    this.$t('inviteToDashboardMassAction.countryCodeRequired');
                  return false;
                }

                return true;
              },
            },
            phone: {
              phoneRequired: async (model, parentModel) => {
                //Not required if there's an email on row && no phone code selected
                const isAllModel = parentModel.all ? true : false;
                const hasEmail = parentModel?.email?.length ? true : false;
                const hasPhoneCode = parentModel.phone_code ? true : false;
                const hasPhoneNumber = model.length ? true : false;
                // const isRequired = !hasEmail || hasPhoneCode;
                const userIndex =
                  this.getPreviousStateForUserIndex(parentModel);

                if (!isAllModel)
                  this.tableData[userIndex].invalidPhoneMessage = "";

                if (
                  isAllModel ||
                  (hasEmail && !hasPhoneCode && !hasPhoneNumber)
                ) {
                  return true;
                }

                //Phone is required if it has no value and country code has value
                if (hasPhoneCode && !hasPhoneNumber) {
                  this.tableData[userIndex].invalidPhoneMessage =
                    this.$t('inviteToDashboardMassAction.phoneRequired');
                  return false;
                }

                if (!hasPhoneNumber && !hasEmail) return false;
                else if (hasPhoneNumber) {
                  //Check phone value with previous state (api state)
                  const userPreviousState =
                    this.getPreviousStateForUser(parentModel);

                  if (
                    userPreviousState?.phone &&
                    userPreviousState?.phone == model &&
                    hasPhoneNumber &&
                    !hasPhoneCode
                  ) {
                    return true;
                  }

                  //Check for duplicates
                  let phoneAlreadyUsedOnForm = [];
                  phoneAlreadyUsedOnForm = this.tableData.filter(
                    (userData, indx) => {
                      return userData.phone == model && indx != userIndex;
                    }
                  );
                  if (phoneAlreadyUsedOnForm.length) {
                    this.tableData[userIndex].invalidPhoneMessage =
                      this.$t('inviteToDashboardMassAction.phoneAlreadyUsed');
                    return false;
                  }

                  //Check if phone number is unique
                  let isAvailable = false;
                  let formData = new FormData();
                  formData.append("entity_id", this.currentEntity.id);
                  formData.append("field", "phone");
                  let searchValue = parentModel?.phone_code?.dial_code
                    ? `${parentModel.phone_code.dial_code}${model}`
                    : model;
                  formData.append("value", searchValue);
                  await this.fetchUsersByEmailOrPhone(formData).then((data) => {
                    isAvailable = data.data.available;
                  });
                  if (!isAvailable) {
                    this.tableData[userIndex].invalidPhoneMessage =
                    this.$t('inviteToDashboardMassAction.phoneAlreadyUsed');
                  }
                  return isAvailable;
                }
              },
            },
          },
        },
      };
    }
  },
  computed: {
    ...mapGetters("users", ["getInvitedUsersData", "massActionLoader"]),
    ...mapGetters("authfack", ["currentEntity"]),
  },
  methods: {
    ...mapActions("users", [
      "clearInvitedUsersData",
      "massactionApplyInviteUsers",
    ]),
    ...mapActions("visit", ["fetchUsersByEmailOrPhone"]),
    async inviteAll() {
      this.massActionLoaderStatus = true;
      this.massActionIsStarted = true;
      this.tableData.forEach((userData) => {
        if (!userData.all) userData.status = this.$t('signInOut.massAction.status.pending');
      });
      for (const userData of this.tableData) {
        if (!userData.all) {
          userData.status = this.$t('signInOut.massAction.status.inProgress');
          const payloadObject = {
            id: userData.id,
            email: userData.email,
            phone:
              userData.phone && userData.phone_code
                ? `${userData.phone_code?.dial_code}${userData.phone}`
                : userData.phone && !userData.phone_code
                ? userData.phone
                : "",
            role_id: userData.role_id.id,
            system_user: "1",
            sites: userData.sites,
            userTypeIds: userData.user_types,
          };
          await this.massactionApplyInviteUsers(payloadObject);
          userData.status = this.$t('signInOut.massAction.status.completed');
        }
      }
      this.massActionLoaderStatus = false;
      this.$bvModal.show("redirectModal");
    },
    siteMassAction(sites) {
      this.tableData.forEach((userData) => {
        if (!userData.all) {
          this.tableData.forEach((userData, indx) => {
            if (!userData.all) {
              this.$v.tableData.$each.$iter[indx].sites.$touch();
            }
          });
          if (sites?.length) {
            userData.sites = sites;
          }
        }
      });
    },
    siteMassActionDeselect() {
      if (this.tableData[0].sites?.length == 1) {
        this.tableData.forEach((userData) => {
          userData.sites = null;
        });
      }
    },
    userTypeMassAction(userTypes) {
      this.tableData.forEach((userData) => {
        if (!userData.all) {
          this.tableData.forEach((userData, indx) => {
            if (!userData.all) {
              this.$v.tableData.$each.$iter[indx].user_types.$touch();
            }
          });
          if (userTypes?.length) {
            userData.user_types = userTypes;
          }
        }
      });
    },
    userTypeMassActionDeselect() {
      if (this.tableData[0].user_types?.length == 1) {
        this.tableData.forEach((userData) => {
          userData.user_types = null;
        });
      }
    },
    updateAllUsers(role) {
      if (!role)
        this.tableData.forEach((userData) => (userData.role_id = null));

      this.tableData.forEach((userData) => {
        if (!userData.all) {
          this.tableData.forEach((userData, indx) => {
            if (!userData.all) {
              this.$v.tableData.$each.$iter[indx].role_id.$touch();
            }
          });
          if (role?.id) {
            userData.role_id = role;
          }
          // else {
          //   userData.role_id = null;
          // }
        }
      });
    },
    updateAllRole(role) {
      if (role?.id != this.tableData[0]?.role_id?.id)
        this.tableData[0].role_id = null;
      this.userRoleIsTouched = true;
    },
    updateAllSites(sites) {
      if (
        difference(this.tableData[0].sites, sites)?.length ||
        difference(sites, this.tableData[0].sites)?.length
      )
        this.tableData[0].sites = null;
    },
    updateAllUsertypes(userTypes) {
      if (
        difference(this.tableData[0].user_types, userTypes)?.length ||
        difference(userTypes, this.tableData[0].user_types)?.length
      )
        this.tableData[0]?.user_types?.splice(0);
    },
    checkForErrors(userData) {
      if (userData.email == "" && userData.phone == "")
        userData.error = "Email address or phone number is required.";
      else userData.error = "";
      this.$forceUpdate();
    },

    async checkDuplicatedEmailOrPhone(type, value) {
      let isAvailable = false;
      let formData = new FormData();
      formData.append("entity_id", this.currentEntity.id);
      formData.append("field", type);
      formData.append("value", value);
      await this.fetchUsersByEmailOrPhone(formData).then((data) => {
        isAvailable = data.data.available;
      });
      return isAvailable;
    },
    getPreviousStateForUser(currentUserData) {
      return this.tableDataPreviousState?.find(
        (userData) => userData.id == currentUserData?.id
      );
    },
    getPreviousStateForUserIndex(currentUserData) {
      return this.tableDataPreviousState?.findIndex(
        (userData) => userData.id == currentUserData?.id
      );
    },
    touchCurrentField(userData, field) {
      const userDataIndex = this.getPreviousStateForUserIndex(userData);
      this.$v.tableData.$each.$iter[userDataIndex][field].$touch();
    },
    setIsEmailInputeBlured(val) {
      this.isEmailInputeBlured = val;
    },
    setIsPhoneInputBlured(val) {
      this.isPhoneInputBlured = val;
    },
    onMultiselectOpen() {
      setTimeout(() => {
        const lastMultiselectActive = document.querySelector(
          "tbody>tr:nth-last-child(1) .multiselect--active"
        );
        const lastMultiselectActive_2 = document.querySelector(
          "tbody>tr:nth-last-child(2) .multiselect--active"
        );
        const lastMultiselectActive_3 = document.querySelector(
          "tbody>tr:nth-last-child(3) .multiselect--active"
        );
        const table = document.querySelector(".md-table-content");
        if (lastMultiselectActive) {
          table.classList.add("pb-120");
        } else if (lastMultiselectActive_2) {
          table.classList.add("pb-70");
        } else if (lastMultiselectActive_3) {
          table.classList.add("pb-20");
        }
      }, 1);
    },
    onMultiselectClose() {
      const table = document.querySelector(".md-table-content");
      table.classList.remove("pb-145", "pb-120", "pb-70", "pb-20");
    },
    onTreeselectOpen() {
      setTimeout(() => {
        const lastTreeselectActive = document.querySelector(
          "tbody>tr:nth-last-child(1) .vue-treeselect--open"
        );
        const lastTreeselectActive_2 = document.querySelector(
          "tbody>tr:nth-last-child(2) .vue-treeselect--open"
        );
        const lastTreeselectActive_3 = document.querySelector(
          "tbody>tr:nth-last-child(3) .vue-treeselect--open"
        );
        const table = document.querySelector(".md-table-content");
        if (lastTreeselectActive) {
          table.classList.add("pb-145");
        } else if (lastTreeselectActive_2) {
          table.classList.add("pb-70");
        } else if (lastTreeselectActive_3) {
          table.classList.add("pb-20");
        }
      }, 1);
    },
    onTreeselectClose() {
      this.onMultiselectClose();
    },
  },
  async beforeMount() {
    this.tableData = cloneDeep(this.getInvitedUsersData.users);
    this.tableDataPreviousState = cloneDeep(this.getInvitedUsersData.users);
    this.roleOptions = this.getInvitedUsersData.roles;
    this.isLoaded = true;
  },
  async mounted() {
    await Promise.all([
      siteService.getSites(),
      userTypeService.getAllParentsUsersTypes(),
    ]).then((values) => {
      this.sitesOptions =
        values[0]?.data?.map((site) => {
          Object.assign(site, { label: site.name });
          return site;
        }) || [];
      this.userTypesOptions = values[1]?.data || [];
    });
  },
  beforeCreate() {
    // Redirect to users if user access page by typing route url
    if (!this.$router?.history?.current?.params?.inviteuserstodashboard) {
      window.location.href = "/users";
    }
  },

  destroyed() {
    this.clearInvitedUsersData();
  },
};
window.onbeforeunload = function () {
  let vuex = JSON.parse(localStorage.getItem("vuex"));
  vuex.users.invitedUsersData = {};
  localStorage.setItem("vuex", JSON.stringify(vuex));
};
</script>

<style lang="scss" scoped>
::v-deep {
  .display-contents {
    display: contents;
  }

  .dimmed {
    opacity: 0.4 !important;
  }
  .md-table-cell:has(
      .multiselect--active,
      .vue-treeselect--focused,
      .email-input:focus,
      .phone-input:focus
    ) {
    border-radius: 5px !important;
    border: 3px solid #103f5e;
    margin: -25px -2px;
    padding: 1px 0;
  }
  span.multiselect__option {
    white-space: break-spaces;
    line-height: 19px;
  }
  .pages-title {
    color: #4a4a4a !important;
    font-weight: 400 !important;
    font-size: 16px !important;
    line-height: 19px !important;

    padding-bottom: 10px !important;
    padding-top: 18px !important;
    margin-inline-start: 16px !important;
  }
  .userlist-massaction {
    table {
      overflow: auto;
      margin-bottom: 10px;
    }

    .md-field.md-theme-default.md-has-value.md-has-placeholder {
      .email-input:focus,
      .phone-input:focus {
        font-weight: 400;
        font-size: 16px;
        line-height: 17px !important;
        letter-spacing: 0.25px;
        color: rgba(96, 96, 96, 0.87) !important;
        opacity: 0.8 !important;
        width: 195px;
      }
    }

    .multiselect__content > li {
      border-bottom: 1px solid #f0f0f0;
      .multiselect__option {
        color: #787878 !important;
        font-weight: 400 !important;
        font-size: 16px !important;
      }
      .multiselect__option--highlight {
        color: #ffffff !important;
      }
    }

    .md-table-cell-container {
      .multiselect__select:before {
        border-color: #636161 transparent transparent !important;
      }
      span.multiselect__single {
        font-weight: 400;
        font-size: 16px !important;
        line-height: 19px !important;
        margin-inline-start: -25px !important;
        margin-top: 4px !important;
        color: #495057 !important;
        white-space: nowrap;
      }
    }
    input.multiselect__input {
      margin-inline-start: -25px !important;
      color: rgba(96, 96, 96, 0.87) !important;
      &::placeholder {
        font-weight: 400 !important;
        font-size: 16px !important;
        line-height: 17px !important;
        letter-spacing: 0.25px;
        color: rgba(96, 96, 96, 0.87) !important;
        opacity: 0.8 !important;
      }
    }

    .multiselect__tags {
      span.multiselect__placeholder {
        font-weight: 400;
        font-size: 16px;
        line-height: 19px;
        color: #495057 !important;
        margin-inline-start: -25px !important;
        margin-top: 4px !important;
      }
    }
    .md-content.md-table-content.md-scrollbar.md-theme-default {
      margin-inline-start: 20px;
    }

    .invaled-email-color {
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      color: #f46a6a;
    }

    .email-input,
    .phone-input {
      width: 150px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      color: #495057 !important;
      padding-inline-end: 10px;
    }

    td.md-table-cell {
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      color: #495057 !important;
    }

    .md-table-head-label {
      font-weight: 400;
      font-size: 14px;
      line-height: 17px;
      padding-inline-start: 3px !important;
      color: #939393;
      padding-inline-start: 25px !important;
    }
  }

  .userlist-massaction {
    .page-container {
      margin-inline-start: 12px;
      margin-top: 20px;
      min-height: 80vh;
      background-color: #fff;
      border-radius: 8px;
    }
    .massaction-invite-toolbar {
      width: 277px;
      margin-top: 24px !important;
      margin-inline-start: 40px !important;
      min-height: 74px !important;
      padding: 0px !important;
      display: flex;
      align-items: center;
      p.md-title-1 {
        font-weight: 400;
        font-size: 14px;
        line-height: 17px;
        margin-top: 0px !important;
        margin-inline-end: 16px !important;
        margin-bottom: 0px !important;
        margin-inline-start: 0px !important;
        color: rgba(147, 147, 147, 0.87);
      }
      button.invite-all {
        background: #2680eb !important;
        min-width: 82px !important;
        height: 28px !important;
        // opacity: 0.4;

        font-weight: 400;
        font-size: 14px;
        line-height: 17px;
        /* identical to box height */

        text-align: center;

        color: #ffffff;
        border: none;
        border-radius: 13.5px;
        &:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
      }
      button.invite-loading {
        width: 93px !important;
      }
      button.invite-loading:lang(ar){
        padding: 6px;
        width: fit-content;
      }
    }
    table {
      border-top: 1px solid #f2f2f2;
      border-bottom: 1px solid #f2f2f2;
    }
    .md-table-cell,
    .md-table-head {
      border-inline-end: 1px solid #f2f2f2;
    }
    tbody .md-table-row td,
    .md-table-head-container {
      margin-inline-end: -0.5px;
    }
    tbody .md-table-row td {
      border-top: 1px solid #f2f2f2;
      padding: 2px;
    }
    tbody .md-table-row td .md-table {
      width: auto;
    }
    .md-table-cell-container {
      min-width: 150px;
      padding: 0;
      margin-bottom: -5px;
      margin-top: -4px;
    }
    .cell-min-height {
      min-height: 35px;
    }
    .w-352 {
      min-width: 352px;
    }
    .w-176 {
      min-width: 176px;
      // padding-inline-start: 40px!important;
    }
    .w-135 {
      min-width: 176px;
      // padding-inline-start: 40px!important;
    }
    .w-158 {
      min-width: 158px;
      // padding-inline-start: 25px!important;
    }
    .w-169 {
      min-width: 169px;
      // padding-inline-start: 25px!important;
    }
    .w-195 {
      min-width: 195px;
      // padding-inline-start: 25px!important;
    }
    table td {
      vertical-align: middle;
    }

    .table-cell-margin p {
      margin-inline-start: 22px;
      color: #495057;
    }

    .table-cell-margin span {
      padding-inline-start: 22px;
    }

    // .phone-input{
    //   padding-inline-start: 22px;
    // }

    .md-field {
      padding: 0;
      margin: 0;
    }

    .md-input {
      height: 47px;
      display: inline-block;
    }

    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    input[type="number"] {
      -moz-appearance: textfield;
    }

    .multiselect__select {
      z-index: 1;
    }

    .country-code-dropdown,
    .user-role-dropdown {
      .multiselect__tags {
        border: none !important;
        border-radius: 0 !important;
        height: 41px !important;
        padding: 0.47rem 1.9rem !important;
      }
    }

    .multiselect__option--selected:after {
      content: none !important;
    }
    ::placeholder {
      color: #495057;
      //  padding-inline-start:15px;
    }
    input:focus::placeholder {
      color: transparent;
    }
    .md-table-row {
      /* width */
      ::-webkit-scrollbar {
        width: 10px;
        height: 30px;
      }

      /* Track */
      ::-webkit-scrollbar-track {
        background: #c7c7c7;
        border-radius: 8.5px;
        -webkit-box-shadow: #ffff;
        margin: 0 2px;
      }

      /* Handle */
      ::-webkit-scrollbar-thumb {
        background: #7d7d7d;
        border-radius: 5px;
        height: 10%;
      }

      /* Handle on hover */
      ::-webkit-scrollbar-thumb:hover {
        background: #555;
      }

      .md-table-cell {
        transition: border 0s !important;
      }

      // .md-table-cell:focus-within {
      // border: 2px solid #103f5e !important;
      // }

      .status-completed,
      .status-in-progress,
      .status-pending {
        font-size: 16px;
        font-weight: 700;
        line-height: 19px;
        letter-spacing: 0em;
      }
      .status-completed {
        color: #34c38f;
      }
      .status-in-progress {
        color: #2680eb;
      }
      .status-pending {
        color: #ffa500;
      }
      .full-opacity {
        opacity: 1;
      }
      .invalid-cell {
        border: 3px solid #f46a6a !important;
        border-radius: 5px !important;
        // background-color: red;
      }
      .invalid-multiselect {
        // height: 47px;
        margin: -25px -1px;
        border: 3px solid #f46a6a !important;
        border-radius: 5px !important;
        padding: 1px 0;
      }
      .invalid-input {
        border: 2px solid #f46a6a !important;
        min-width: 195px;
        margin: 0 -2px 0 -2px;
        border-radius: 5px !important;
        // padding: 5px;
      }

      .table-cell-input-pading input {
        padding-inline-start: 15px;
      }

      .user-role-th,
      .country-code-th {
        width: 10px;
        max-width: 120px !important;
      }
    }
  }
}
</style>
<style lang="scss">
section.userlist-massaction {
  //.vue-treeselect__menu {
    //max-height: 200px !important;
  //}
  // .vue-treeselect__option{
  //   display: flex!important;
  //   align-items: center!important;

  // }
  // .vue-treeselect__list{
  //   padding-inline-start: 20px;
  // }
  // .vue-treeselect__label-container{
  //   // display: flex;
  //   // align-items: center;
  //   // padding-inline-start: 15px!important;
  // }

  .vue-treeselect__list-item.vue-treeselect__indent-level-1 {
    // padding-inline-start: 15px;
    // width: 800px;
    .vue-treeselect__checkbox-container {
      padding-inline-start: 20px !important;
      padding-inline-end: 14px !important;
    }
  }
  .multiselect__content-wrapper {
    max-height: 130px !important;
  }
}
.tooltip .tooltip-inner {
  padding: 10px;
  border-radius: 6px;
  background-color: #f46a6a !important;

  font-weight: 600 !important;
  font-size: 12px !important;
  line-height: 14px !important;
  text-align: center;
  color: #ffffff !important;
  min-width: 123px;
  transition: none !important;
}

.arrow {
  display: none !important;
}

.bs-tooltip-top.b-tooltip-danger {
  margin-inline-start: 10px;
  margin-bottom: 10px !important;
  transition: none !important;
}

.bs-tooltip-bottom.b-tooltip-danger {
  top: -80px !important;
  inset-inline-start: 22px !important;
  transition: none !important;
  will-change: opacity !important;
}

.pb-145 {
  padding-bottom: 145px;
}

.pb-120 {
  padding-bottom: 120px;
}
.pb-70 {
  padding-bottom: 70px;
}
.pb-20 {
  padding-bottom: 20px;
}
</style>
